import type { ThreadError, ThreadErrorType } from '@fruitful/types';

import { CONVERSATIONAL_MM_AGENT, SPROUT_AGENT } from './agents';

export const ERRORS_METRIC_ID = 'errors';
export const PARTICIPANT_TURNS_METRIC_ID = 'participantTurns';
export const AGENT_TURNS_METRIC_ID = 'agentTurns';
export const CONVERSATION_DURATION_MS_METRIC_ID = 'conversationDurationMs';
export const AGENT_RESPONSE_WAIT_MS_METRIC_ID = 'agentResponseWaitMs';
export const LAST_MESSAGE_AT_METRIC_ID = 'lastMessageAt';

export const CMM_BRANCH_METRIC_ID = 'branch';
export const CMM_CURRENT_STEP_METRIC_ID = 'currentStep';
/**
 * Synthetic field: how far the member got through the goals flow, rendered as
 * "completed/total" (e.g. "4/5"). Derived from the stored `stepsCompleted` /
 * `stepsTotal` metrics — see `getThreadMetricValue`.
 */
export const CMM_STEPS_PROGRESS_METRIC_ID = 'stepsProgress';
export const CMM_AHA_MOMENT_HEADLINE_METRIC_ID = 'ahaMoment.headline';
export const CMM_AHA_MOMENT_GOAL_NAME_METRIC_ID = 'ahaMoment.goalName';
export const CMM_CHAT_VERSION_METRIC_ID = 'chatVersion';
export const CMM_ARTIFACT_VERSION_METRIC_ID = 'artifactVersion';

export const SPROUT_CHARTS_SHOWN_METRIC_ID = 'chartsShown';
export const SPROUT_TABLES_SHOWN_METRIC_ID = 'tablesShown';
export const SPROUT_HANDOFF_METRIC_ID = 'handoff';
export const SPROUT_OFF_TOPIC_FLAGS_METRIC_ID = 'offTopicFlags';

export const UNIVERSAL_THREAD_METRIC_IDS = [
    PARTICIPANT_TURNS_METRIC_ID,
    AGENT_TURNS_METRIC_ID,
    CONVERSATION_DURATION_MS_METRIC_ID,
    AGENT_RESPONSE_WAIT_MS_METRIC_ID,
    LAST_MESSAGE_AT_METRIC_ID,
    ERRORS_METRIC_ID,
] as const;

export const CMM_THREAD_METRIC_IDS = [
    CMM_CHAT_VERSION_METRIC_ID,
    CMM_ARTIFACT_VERSION_METRIC_ID,
    CMM_BRANCH_METRIC_ID,
    CMM_CURRENT_STEP_METRIC_ID,
    CMM_STEPS_PROGRESS_METRIC_ID,
    CMM_AHA_MOMENT_HEADLINE_METRIC_ID,
    CMM_AHA_MOMENT_GOAL_NAME_METRIC_ID,
] as const;

export const SPROUT_THREAD_METRIC_IDS = [
    SPROUT_CHARTS_SHOWN_METRIC_ID,
    SPROUT_TABLES_SHOWN_METRIC_ID,
    SPROUT_HANDOFF_METRIC_ID,
] as const;

export const THREAD_METRIC_DISPLAY_NAMES: Record<string, string> = {
    [ERRORS_METRIC_ID]: 'Error',
    [PARTICIPANT_TURNS_METRIC_ID]: 'Member Turns',
    [AGENT_TURNS_METRIC_ID]: 'Agent Turns',
    [CONVERSATION_DURATION_MS_METRIC_ID]: 'Duration',
    [AGENT_RESPONSE_WAIT_MS_METRIC_ID]: 'Wait Time',
    [LAST_MESSAGE_AT_METRIC_ID]: 'Last Message',
    [CMM_BRANCH_METRIC_ID]: 'Branch',
    [CMM_CURRENT_STEP_METRIC_ID]: 'Current Step',
    [CMM_STEPS_PROGRESS_METRIC_ID]: 'Steps',
    [CMM_AHA_MOMENT_HEADLINE_METRIC_ID]: 'Aha Moment',
    [CMM_AHA_MOMENT_GOAL_NAME_METRIC_ID]: 'Goal',
    [CMM_CHAT_VERSION_METRIC_ID]: 'Chat Version',
    [CMM_ARTIFACT_VERSION_METRIC_ID]: 'Artifact Version',
    [SPROUT_CHARTS_SHOWN_METRIC_ID]: 'Charts',
    [SPROUT_TABLES_SHOWN_METRIC_ID]: 'Tables',
    [SPROUT_HANDOFF_METRIC_ID]: 'Handoff',
    [SPROUT_OFF_TOPIC_FLAGS_METRIC_ID]: 'Off Topic',
};

export const getThreadMetricDisplayName = (metricFieldId: string): string => {
    return THREAD_METRIC_DISPLAY_NAMES[metricFieldId] ?? metricFieldId;
};

export const getThreadErrorTypeDisplayName = (error: ThreadError): string => {
    switch (error.type) {
        case 'tool':
            return `Tool: ${error.name}`;
        case 'stream':
            return 'Stream error';
        case 'noReply':
            return 'No reply';
        case 'emptyReply':
            return 'Empty reply';
        case 'slowReply':
            return 'Slow reply';
        case 'nudge':
            return 'Nudge';
        case 'loop':
            return 'Loop';
        case 'multipleReplies':
            return 'Multiple replies';
        case 'moneyUnit':
            return 'Money unit';
        default: {
            const _exhaustive: never = error;
            return _exhaustive;
        }
    }
};

export const getThreadErrorTypeLabel = (errorType: ThreadErrorType): string => {
    switch (errorType) {
        case 'tool':
            return 'Tool';
        case 'stream':
            return 'Stream';
        case 'noReply':
            return 'No reply';
        case 'emptyReply':
            return 'Empty reply';
        case 'slowReply':
            return 'Slow reply';
        case 'nudge':
            return 'Nudge';
        case 'loop':
            return 'Loop';
        case 'multipleReplies':
            return 'Multiple replies';
        case 'moneyUnit':
            return 'Money unit';
        default: {
            const _exhaustive: never = errorType;
            return _exhaustive;
        }
    }
};

export const getThreadErrorTypeDescription = (
    errorType: ThreadErrorType,
): string => {
    switch (errorType) {
        case 'tool':
            return 'A tool call failed and was not retried successfully.';
        case 'stream':
            return 'The model stream failed, or the member saw a generic “something went wrong” reply.';
        case 'noReply':
            return 'The last member message has no later assistant reply.';
        case 'emptyReply':
            return 'An assistant message was persisted with no member-visible text, widget, or successful tool output.';
        case 'slowReply':
            return 'An assistant reply took more than 30 seconds after the member’s message.';
        case 'nudge':
            return 'The member had to prompt the agent to respond (e.g. “you there?”).';
        case 'loop':
            return 'The agent repeated itself: a doubled message, the same reply on consecutive turns, or a run of bare acknowledgments.';
        case 'multipleReplies':
            return 'The agent sent more than one assistant message in the same member turn.';
        case 'moneyUnit':
            return 'Assistant prose stated integer cents as dollars (a 100× money-unit error).';
        default: {
            const _exhaustive: never = errorType;
            return _exhaustive;
        }
    }
};

/** Metric field ids shown on the threads dashboard and metrics panel. */
export const getThreadMetricFieldIdsForAgent = (
    agentId: string,
): readonly string[] => {
    if (agentId === CONVERSATIONAL_MM_AGENT.id) {
        return [...UNIVERSAL_THREAD_METRIC_IDS, ...CMM_THREAD_METRIC_IDS];
    }

    if (agentId === SPROUT_AGENT.id) {
        return [...UNIVERSAL_THREAD_METRIC_IDS, ...SPROUT_THREAD_METRIC_IDS];
    }

    return [...UNIVERSAL_THREAD_METRIC_IDS];
};
